import { Skeleton, type UploadFile } from "antd"
import React, {
  Suspense,
  forwardRef,
  lazy,
  useImperativeHandle,
  useRef,
  useState
} from "react"

import { type Attachment } from "../../../../../Album/types/attachment.types.ts"
import { ProjectFileCard } from "../../ProjectFileCard.tsx"
import { ProjectViewAlbumAttachmentsUpload } from "./ProjectViewAlbumAttachmentsUpload.tsx"

const FilePreviewModal = lazy(
  () =>
    import("../../../../../../components/FilePreviewModal/FilePreviewModal.tsx")
)

export interface ProjectViewAlbumAttachmentsRef {
  openUpload: () => void
}

interface ProjectViewAlbumAttachmentsProps {
  fileList: UploadFile<Attachment>[]
  deletingIds: Set<string>
  addFilesAllowed: boolean
  currentUserId: string
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
  editAllowed: boolean
  editForeignAllowed: boolean
  allowedExtensions?: string[]
  maxFileSize?: number
  onRemoveAttachment: (id: string) => Promise<void>
  onRename: (attachmentId: string, nextFileName: string) => Promise<void>
  onBeforeUpload?: (file: File) => boolean
  // onCancelUpload?: (uid: string) => void
}

export const ProjectViewAlbumAttachments = forwardRef<
  ProjectViewAlbumAttachmentsRef,
  ProjectViewAlbumAttachmentsProps
>(
  (
    {
      currentUserId,
      fileList,
      deletingIds,
      addFilesAllowed,
      deleteForeignAllowed,
      deleteAllowed,
      editAllowed,
      editForeignAllowed,
      allowedExtensions = ["png", "jpeg", "jpg", "pdf", "doc", "docx"],
      maxFileSize,
      onRemoveAttachment,
      onRename,
      onBeforeUpload
      // onCancelUpload
    },
    ref
  ) => {
    const [previewFile, setPreviewFile] = useState<UploadFile | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
      openUpload: () => {
        inputRef.current?.click()
      }
    }))

    return (
      <>
        {previewFile?.url && (
          <Suspense
            fallback={
              <Skeleton.Node
                active
                className={"fixed z-10 m-auto inset-0 h-[50vh] w-[50vw]"}
              />
            }
          >
            <FilePreviewModal
              visible
              onClose={() => setPreviewFile(null)}
              doc={{
                file: previewFile?.url,
                file_name: previewFile?.fileName,
                name: previewFile?.name
              }}
            />
          </Suspense>
        )}

        {addFilesAllowed && (
          <ProjectViewAlbumAttachmentsUpload
            ref={inputRef}
            allowedExtensions={allowedExtensions}
            maxFileSize={maxFileSize}
            onBeforeUpload={onBeforeUpload}
          />
        )}

        {/* File Cards */}
        {fileList?.map((file) => (
          <div key={file.uid} className={"min-h-64"}>
            <ProjectFileCard
              currentUserId={currentUserId}
              file={file}
              deleteAllowed={deleteAllowed}
              deleteForeignAllowed={deleteForeignAllowed}
              editAllowed={editAllowed}
              editForeignAllowed={editForeignAllowed}
              deleting={deletingIds.has(file.uid)}
              onPreviewFile={setPreviewFile}
              onDelete={onRemoveAttachment}
              onRename={onRename}
              // onCancelUpload={onCancelUpload}
            />
          </div>
        ))}
      </>
    )
  }
)

ProjectViewAlbumAttachments.displayName = "ProjectViewAlbumAttachments"
